import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiGrid, FiTrash2, FiPlay, FiSquare } from "react-icons/fi";
import GameServices from "../../../services/GameServices";
import Loader from "../../../components/Loader";
import NoData from "../../../components/NoData";
import GameFormModal from "./GameFormModal";
import GameYantraModal from "./GameYantraModal";
import ConfirmActionModal from "./ConfirmActionModal";

/** Copy for each confirmation, keyed by the action being confirmed. */
const CONFIRM = {
  start: {
    tone: "success",
    title: "Start game",
    confirmText: "Yes, Start",
    busyText: "Starting...",
    message: (g) => `Start ${g.game_name}? Rounds will be generated and players can bet on it.`,
  },
  stop: {
    tone: "warning",
    title: "Stop game",
    confirmText: "Yes, Stop",
    busyText: "Stopping...",
    message: (g) => `Stop ${g.game_name}? No new bets and no future rounds.`,
    detail: "The round already running settles normally and all history stays available.",
  },
  delete: {
    tone: "danger",
    title: "Delete game",
    confirmText: "Yes, Delete",
    busyText: "Deleting...",
    message: (g) => `Delete ${g.game_name}?`,
    detail: "Past rounds, bets and reports are kept — the game is only hidden from this list.",
  },
};

/** Slug -> name, for the table. The form loads the full list from the API. */
const ANIMATION_NAMES = {
  default: "Default",
  highlight: "Highlight",
  royal_orbit: "Royal Orbit",
  spiral_path: "Spiral Path",
  slot_machine: "Slot Machine",
  flip_cards: "Flip Cards",
  royal_winner: "Royal Winner",
  coin_crash: "Coin Crash",
  neon_portal: "Neon Energy Portal",
};

export default function GamesSettings() {
  const gameApi = new GameServices();

  const [games, setGames] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formGame, setFormGame] = useState(null); // {} = create, {id} = edit
  const [boardGame, setBoardGame] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, game }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const loadGames = useCallback(() => {
    setLoading(true);
    gameApi
      .getGames()
      .then((res) => {
        if (res?.isSuccess) setGames(res.data || []);
        else toast.error(res?.message || "Could not load games");
      })
      .catch(() => toast.error("Could not load games"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = useCallback(() => {
    gameApi
      .getOptions()
      .then((res) => {
        if (res?.isSuccess) setOptions(res.data);
      })
      .catch(() => {
        /* the form falls back to safe defaults */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadGames();
    loadOptions();
  }, [loadGames, loadOptions]);

  const runConfirm = async () => {
    if (!confirm) return;
    const { action, game } = confirm;

    setConfirmBusy(true);
    try {
      const res =
        action === "delete"
          ? await gameApi.deleteGame(game.id)
          : await gameApi.toggleActive(game.id, action === "start");

      if (res?.isSuccess) {
        toast.success(res.message);
        setConfirm(null);
        loadGames();
        loadOptions();
      } else {
        toast.error(res?.message || "Action failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setConfirmBusy(false);
    }
  };

  const activeCount = games.filter((g) => g.is_active).length;
  const maxActive = options?.max_active_games ?? 6;
  const confirmCopy = confirm ? CONFIRM[confirm.action] : null;

  /**
   * A real `disabled` button swallows the click, so an unavailable action
   * can never say why it is unavailable. These stay clickable and answer
   * with the reason instead.
   */
  const blockedProps = (reason, base) => ({
    className: `${base} is-blocked`,
    "aria-disabled": true,
    title: reason,
    onClick: () => toast.error(reason),
  });

  return (
    <>
      <div className="gm-head">
        <div>
          <h5 className="settings-section-title">Games</h5>
          <p className="settings-section-sub">
            Duration, board size and amounts are set per game.{" "}
            <b>{activeCount} running</b> (limit {maxActive}).
          </p>
        </div>
        <button className="btn gm-btn-primary" onClick={() => setFormGame({})}>
          <FiPlus size={15} /> Add Game
        </button>
      </div>

      {loading ? (
        <Loader message="Loading games..." />
      ) : games.length === 0 ? (
        <NoData
          message="No Games Yet"
          subText="Add a game to set its round length, board size and amounts."
        />
      ) : (
        // Scrolls inside itself rather than pushing the page sideways.
        <div className="gm-tw">
          <table className="gm-table">
            <thead>
              <tr>
                <th>Game</th>
                <th className="num">Duration</th>
                <th className="num">Ghar</th>
                <th className="num">Rounds/day</th>
                <th className="num">Bet unit</th>
                <th className="num">Winning</th>
                <th className="num">Difference</th>
                <th className="num">Bet close</th>
                <th>Animation</th>
                <th className="num">Board</th>
                <th>Status</th>
                <th className="gm-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.map((game) => {
                const filled = Number(game.slots_filled) || 0;
                const total = Number(game.total_ghar) || 0;
                const complete = filled === total;

                // Why each action is unavailable, or null when it is fine.
                // Config changes on a running game would land mid-round,
                // against bets already placed under the old settings.
                const editBlocked = game.is_active
                  ? `${game.game_name} is live. Stop it first, then edit.`
                  : null;
                const deleteBlocked = game.is_active
                  ? `${game.game_name} is live. Stop it first, then delete.`
                  : null;
                const startBlocked = complete
                  ? null
                  : `Fill all ${total} yantra slots before starting ${game.game_name} (${filled} filled).`;

                return (
                  <tr key={game.id} className={game.is_active ? "is-live" : ""}>
                    <td>
                      <span className="gm-name">{game.game_name}</span>
                      <span className="gm-code">{game.game_code}</span>
                    </td>
                    <td className="num">{game.duration_minutes} min</td>
                    <td className="num">{total}</td>
                    <td className="num">{game.sessions_per_day}</td>
                    <td className="num">{game.yantra_amount}</td>
                    <td className="num">{game.winning_amount}</td>
                    <td className="num">{game.difference_amount}</td>
                    <td className="num">{game.bet_close_seconds} s</td>
                    <td className="gm-anim">
                      {ANIMATION_NAMES[game.winner_animation] ||
                        game.winner_animation ||
                        "Highlight"}
                    </td>
                    <td className={`num ${complete ? "gm-ok" : "gm-warn"}`}>
                      {filled}/{total}
                    </td>
                    <td>
                      <span
                        className={`gm-badge ${
                          game.is_active
                            ? "gm-badge-live"
                            : complete
                              ? "gm-badge-stopped"
                              : "gm-badge-draft"
                        }`}
                      >
                        {game.is_active ? "LIVE" : complete ? "STOPPED" : "DRAFT"}
                      </span>
                    </td>
                    <td className="gm-actions-col">
                      <div className="gm-actions">
                        <button
                          aria-label={`Edit ${game.game_name}`}
                          {...(editBlocked
                            ? blockedProps(editBlocked, "gm-icon-btn")
                            : {
                                className: "gm-icon-btn",
                                title: "Edit game",
                                onClick: () => setFormGame(game),
                              })}
                        >
                          <FiEdit2 size={13} />
                        </button>

                        <button
                          className="gm-icon-btn"
                          onClick={() => setBoardGame(game)}
                          title="Edit yantra board"
                          aria-label={`Yantra board for ${game.game_name}`}
                        >
                          <FiGrid size={13} />
                        </button>

                        <button
                          aria-label={`Delete ${game.game_name}`}
                          {...(deleteBlocked
                            ? blockedProps(deleteBlocked, "gm-icon-btn gm-icon-danger")
                            : {
                                className: "gm-icon-btn gm-icon-danger",
                                title: "Delete game",
                                onClick: () => setConfirm({ action: "delete", game }),
                              })}
                        >
                          <FiTrash2 size={13} />
                        </button>

                        {game.is_active ? (
                          <button
                            className="gm-pill gm-pill-stop"
                            onClick={() => setConfirm({ action: "stop", game })}
                          >
                            <FiSquare size={10} /> STOP
                          </button>
                        ) : (
                          <button
                            {...(startBlocked
                              ? blockedProps(startBlocked, "gm-pill gm-pill-start")
                              : {
                                  className: "gm-pill gm-pill-start",
                                  title: "Start game",
                                  onClick: () => setConfirm({ action: "start", game }),
                                })}
                          >
                            <FiPlay size={10} /> START
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <GameFormModal
        show={Boolean(formGame)}
        game={formGame?.id ? formGame : null}
        options={options}
        onClose={() => setFormGame(null)}
        onSaved={(id) => {
          const wasCreate = !formGame?.id;
          setFormGame(null);
          loadGames();
          loadOptions();
          // A new game has an empty board, so go straight to filling it.
          if (wasCreate && id) {
            gameApi
              .getGames()
              .then((res) => {
                const created = (res?.data || []).find((g) => Number(g.id) === Number(id));
                if (created) setBoardGame(created);
              })
              .catch(() => {});
          }
        }}
      />

      <GameYantraModal
        show={Boolean(boardGame)}
        game={boardGame}
        options={options}
        onClose={() => setBoardGame(null)}
        onSaved={() => {
          setBoardGame(null);
          loadGames();
          loadOptions();
        }}
      />

      <ConfirmActionModal
        show={Boolean(confirm)}
        onClose={() => (confirmBusy ? null : setConfirm(null))}
        onConfirm={runConfirm}
        loading={confirmBusy}
        tone={confirmCopy?.tone}
        title={confirmCopy?.title}
        message={confirm ? confirmCopy.message(confirm.game) : ""}
        detail={confirmCopy?.detail || ""}
        confirmText={confirmCopy?.confirmText}
        busyText={confirmCopy?.busyText}
      />
    </>
  );
}
